import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RemittanceAllocationDto, RemittanceDto, RemittanceSuggestionDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { DataTable, Dialog, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, Input, Select, StatusBadge, statusLabel } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';

const toMinor = (value: string) => minorFromInput(value);

interface GridRow {
  claimId: string;
  claimNumber: string;
  patientName: string | null;
  outstandingMinor: number;
  paid: string;
  deducted: string;
}

function AllocationDialog({ remittanceId, onClose }: { remittanceId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [rows, setRows] = useState<GridRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());
  const detail = useQuery({
    queryKey: ['remittance', remittanceId],
    queryFn: () => get<RemittanceDto & { allocations: RemittanceAllocationDto[] }>(`/insurance/payment-reconciliations/${remittanceId}`),
  });
  const suggest = useMutation({
    mutationFn: () => get<RemittanceSuggestionDto[]>(`/insurance/payment-reconciliations/${remittanceId}/suggest`),
    onSuccess: (list) =>
      setRows(list.map((row) => ({ claimId: row.claimId, claimNumber: row.claimNumber, patientName: row.patientName, outstandingMinor: row.outstandingMinor, paid: formatMoneyInput(row.paidMinor), deducted: '' }))),
    onError: (caught) => setError(describeError(caught)),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['remittance', remittanceId] });
    void queryClient.invalidateQueries({ queryKey: ['remittances'] });
    void queryClient.invalidateQueries({ queryKey: ['claims'] });
  };
  const allocate = useMutation({
    mutationFn: () =>
      post(
        `/insurance/payment-reconciliations/${remittanceId}/allocations`,
        {
          lines: rows.filter((row) => toMinor(row.paid) > 0 || toMinor(row.deducted) > 0).map((row) => ({ claimId: row.claimId, paidMinor: toMinor(row.paid), deductedMinor: toMinor(row.deducted) })),
        },
        { idempotencyKey },
      ),
    onSuccess: () => {
      toast.success('Posted to the claims');
      setIdempotencyKey(newIdempotencyKey());
      setRows([]);
      refresh();
    },
    onError: (caught) => setError(describeError(caught)),
  });
  const unpost = useMutation({
    mutationFn: (claimId: string) => post(`/insurance/payment-reconciliations/${remittanceId}/allocations/${claimId}/unpost`, {}),
    onSuccess: () => {
      toast.success('Posting taken back');
      refresh();
    },
    onError: (caught) => toast.error('Could not take it back', describeError(caught)),
  });
  const data = detail.data;
  const planned = rows.reduce((sum, row) => sum + toMinor(row.paid), 0);

  return (
    <Dialog open wide onClose={onClose} title={data ? `${data.payerName ?? 'Payment'} · ${data.reference}` : 'Payment'} footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      {data && (
        <div className="space-y-4">
          <p className="text-sm">
            Settled {money(data.totalMinor)} · fees {money(data.feeMinor)} · tax withheld {money(data.withholdingTaxMinor)} · posted {money(data.allocatedMinor)} ·{' '}
            <span className="font-medium">left {money(data.unallocatedMinor)}</span>
          </p>
          {data.allocations.length > 0 && (
            <DataTable
              dense
              rows={data.allocations}
              rowKey={(row) => row.id}
              columns={[
                { header: 'Claim', cell: (row) => row.claimNumber },
                { header: 'Paid', align: 'right', cell: (row) => money(row.paidMinor) },
                { header: 'Deducted', align: 'right', cell: (row) => money(row.deductedMinor) },
                { header: '', cell: (row) => <Badge variant={row.status === 'posted' ? 'success' : 'muted'}>{statusLabel(row.status)}</Badge> },
                {
                  header: '',
                  align: 'right',
                  cell: (row) =>
                    row.status === 'posted' && row.claimId && can('remittance.post') && data.source === 'manual' && (
                      <Button size="sm" variant="ghost" loading={unpost.isPending && unpost.variables === row.claimId} onClick={() => unpost.mutate(row.claimId as string)}>
                        Take back
                      </Button>
                    ),
                },
              ]}
            />
          )}
          {data.source === 'manual' && can('remittance.post') && data.unallocatedMinor > 0 && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="me-auto text-sm font-medium">Allocate to claims</p>
                <Button size="sm" variant="outline" loading={suggest.isPending} onClick={() => suggest.mutate()}>
                  Suggest, oldest first
                </Button>
              </div>
              {rows.length > 0 && (
                <DataTable
                  dense
                  rows={rows}
                  rowKey={(row) => row.claimId}
                  columns={[
                    { header: 'Claim', cell: (row) => row.claimNumber },
                    { header: 'Patient', cell: (row) => row.patientName ?? '—' },
                    { header: 'Open', align: 'right', cell: (row) => money(row.outstandingMinor) },
                    {
                      header: 'Paid',
                      align: 'right',
                      cell: (row) => <Input className="w-28" type="text" dir="ltr" inputMode="decimal" value={row.paid} onChange={(e) => setRows(rows.map((entry) => (entry.claimId === row.claimId ? { ...entry, paid: e.target.value } : entry)))} />,
                    },
                    {
                      header: 'Deducted',
                      align: 'right',
                      cell: (row) => <Input className="w-28" type="text" dir="ltr" inputMode="decimal" value={row.deducted} onChange={(e) => setRows(rows.map((entry) => (entry.claimId === row.claimId ? { ...entry, deducted: e.target.value } : entry)))} />,
                    },
                  ]}
                />
              )}
              {error && <Alert variant="destructive">{error}</Alert>}
              {rows.length > 0 && (
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs text-muted-foreground">{money(planned)} of {money(data.unallocatedMinor)}</span>
                  <Button size="sm" loading={allocate.isPending} disabled={planned > data.unallocatedMinor} onClick={() => allocate.mutate()}>
                    Post
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

export function RemittancesTab() {
  const [params] = useSearchParams();
  const [open, setOpen] = useState<string | null>(params.get('remittance'));
  const [status, setStatus] = useState('');
  const list = useQuery({
    queryKey: ['remittances', status],
    queryFn: () => get<{ items: RemittanceDto[] }>(`/insurance/payment-reconciliations${qs({ status: status || undefined, limit: 50 })}`),
  });
  return (
    <div>
      <SearchFilterBar
        filters={
          <FilterField label={tr('billing', 'status')}>
            <Select className="w-52" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Every status</option>
              {['received', 'partially_allocated', 'allocated', 'unmatched', 'needs_review'].map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={status ? () => setStatus('') : undefined}
      />
      <DataTable
        rows={list.data?.items}
        loading={list.isLoading} refreshing={list.isFetching}
        error={list.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setOpen(row.id)}
        empty="No payments recorded yet."
        columns={[
          { header: 'Paid on', sort: (row) => row.paymentDate, cell: (row) => (row.paymentDate ? dateOnly(row.paymentDate) : '—') },
          { header: 'Payer', sort: (row) => row.payerName ?? row.issuerLicense, cell: (row) => row.payerName ?? row.issuerLicense ?? '—' },
          { header: 'Reference', sort: (row) => row.reference, cell: (row) => <span className="font-mono text-xs">{row.reference}</span> },
          { header: 'Amount', align: 'right', sort: (row) => row.totalMinor, cell: (row) => money(row.totalMinor) },
          { header: 'Left', align: 'right', sort: (row) => row.unallocatedMinor, cell: (row) => money(row.unallocatedMinor) },
          { header: 'Source', sort: (row) => row.source, cell: (row) => (row.source === 'nphies' ? 'NPHIES' : 'Recorded here') },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
        ]}
      />
      {open && <AllocationDialog remittanceId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
